import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaBars, FaTimes } from 'react-icons/fa';
import { useConfig } from '@context/ConfigContext';
import styles from './Header.module.css';

const Header: React.FC = () => {
  const { config } = useConfig();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    const element = document.querySelector(targetId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  if (!config) return null;

  const { business, contact, social, components } = config;
  const navItems = [
    { label: 'Home', href: '#home', enabled: components.hero.enabled },
    { label: 'About', href: '#about', enabled: components.about.enabled },
    { label: 'Services', href: '#services', enabled: components.services.enabled },
    { label: 'Portfolio', href: '#portfolio', enabled: components.portfolio.enabled },
    { label: 'Testimonials', href: '#testimonials', enabled: components.testimonials.enabled },
    { label: 'FAQ', href: '#faq', enabled: components.faq.enabled },
    { label: 'Contact', href: '#contact', enabled: components.contact.enabled },
  ].filter((item) => item.enabled);

  const socialPlatforms = [
    { name: 'Facebook', url: social.facebook, icon: 'fab fa-facebook' },
    { name: 'Instagram', url: social.instagram, icon: 'fab fa-instagram' },
    { name: 'LinkedIn', url: social.linkedin, icon: 'fab fa-linkedin' },
    { name: 'Twitter', url: social.twitter, icon: 'fab fa-twitter' },
    { name: 'YouTube', url: social.youtube, icon: 'fab fa-youtube' },
  ].filter((platform) => platform.url);

  return (
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.container}>
        <div className={styles.headerContent}>
          {/* Logo and Business Name */}
          <div className={styles.logoSection}>
            <img src={business.logo} alt={`${business.name} Logo`} className={styles.logo} />
            <div className={styles.businessInfo}>
              <div className={styles.businessName}>{business.name}</div>
              {business.showABN && business.abn && (
                <p className={styles.abn}>ABN: {business.abn}</p>
              )}
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className={styles.nav} aria-label="Main navigation">
            <ul className={styles.navList}>
              {navItems.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={styles.navLink}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact & Social */}
          <div className={styles.headerRight}>
            <a 
              href={`tel:${contact.phoneRaw}`} 
              className={styles.phoneLink}
              onClick={(e) => (e.currentTarget as HTMLAnchorElement).blur()}
            >
              <FaPhone className={styles.phoneIcon} />
              <span className={styles.phoneText}>{contact.phone}</span>
            </a>

            {socialPlatforms.length > 0 && (
              <div className={styles.socialIcons}>
                {socialPlatforms.map((platform) => (
                  <a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={platform.name}
                    className={styles.socialIcon}
                  >
                    <i className={platform.icon} aria-hidden="true"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className={styles.mobileMenuToggle}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          className={styles.mobileMenu}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <nav className={styles.mobileNav}>
            <ul className={styles.mobileNavList}>
              {navItems.map((item) => (
                <motion.li
                  key={item.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <a
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={styles.mobileNavLink}
                  >
                    {item.label}
                  </a>
                </motion.li>
              ))}
            </ul>

            <div className={styles.mobileContact}>
              <a 
                href={`tel:${contact.phoneRaw}`} 
                className={styles.mobilePhoneLink}
                onClick={(e) => (e.currentTarget as HTMLAnchorElement).blur()}
              >
                <FaPhone />
                {contact.phone}
              </a>

              {socialPlatforms.length > 0 && (
                <div className={styles.mobileSocialIcons}>
                  {socialPlatforms.map((platform) => (
                    <a
                      key={platform.name}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={platform.name}
                      className={styles.mobileSocialIcon}
                    >
                      <i className={platform.icon} aria-hidden="true"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </motion.div>
      )}
    </header>
  );
};

export default Header;